import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import FileUpload from "./FileUpload";
import { toast } from "sonner";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";

const inputCls = "w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2";
const textareaCls = "w-full bg-white border border-black/15 focus:border-[#0060A7] outline-none p-3 text-sm leading-relaxed";

const Section = ({ title, hint, children }) => (
    <div className="bg-white p-8 space-y-6">
        <div>
            <h2 className="heading text-lg tracking-tight font-bold">{title}</h2>
            {hint && <p className="text-xs text-[#6B7280] mt-1">{hint}</p>}
        </div>
        {children}
    </div>
);

const Field = ({ label, value, onChange, testid, placeholder }) => (
    <div>
        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{label}</label>
        <input
            className={inputCls}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testid}
        />
    </div>
);

const FieldArea = ({ label, value, onChange, rows = 3, testid }) => (
    <div>
        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{label}</label>
        <textarea
            rows={rows}
            className={textareaCls}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
        />
    </div>
);

const sectionTitleFields = [
    ["about", "About"],
    ["welcome", "Welcome"],
    ["numbers", "Network Numbers"],
    ["global", "Global Reach"],
    ["services", "Services"],
    ["works", "Works"],
    ["news", "News"],
    ["clients", "Clients"],
    ["contact", "Contact"],
];

export default function AdminSiteSettings() {
    const [data, setData] = useState(null);
    const { settings, settingsLoading, refresh } = useSiteSettings();

    useEffect(() => {
        if (!settingsLoading) setData(settings || {});
    }, [settings, settingsLoading]);

    if (!data) return <div className="text-[#6B7280]">…</div>;

    const upd = (k, v) => setData({ ...data, [k]: v });

    const save = async () => {
        try {
            // strip the singleton key before PUT — the model only knows the editable fields
            const { key, _id, updated_at, ...payload } = data;
            const { data: fresh } = await api.put("/admin/site-settings", payload);
            setData(fresh);
            refresh();
            toast.success("تم الحفظ");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="admin-site-settings-page" className="space-y-6 max-w-5xl">
            <div>
                <h1 className="heading text-2xl tracking-tight font-bold">إعدادات الموقع</h1>
                <p className="text-sm text-[#6B7280] mt-1">
                    تحرير الشعار، Hero، معلومات التواصل، روابط السوشيال، وإعدادات SEO. التغييرات تنعكس فوراً على الموقع.
                </p>
            </div>

            <Section title="الشعار و Favicon" hint="ارفع شعار الموقع (يفضّل SVG أو PNG شفاف).">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FileUpload value={data.logo_path} onChange={(v) => upd("logo_path", v)} label="الشعار الأساسي" testid="logo-upload" />
                    <FileUpload value={data.logo_path_dark} onChange={(v) => upd("logo_path_dark", v)} label="الشعار للخلفية الفاتحة (اختياري)" testid="logo-dark-upload" />
                    <FileUpload value={data.favicon_path} onChange={(v) => upd("favicon_path", v)} label="Favicon (32×32)" testid="favicon-upload" />
                </div>
            </Section>

            <Section title="القسم الرئيسي (Hero)" hint="النصوص في أعلى الصفحة الرئيسية.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Title / Frame 1 (AR)" value={data.hero_title_ar} onChange={(v) => upd("hero_title_ar", v)} testid="hero-title-ar" />
                    <Field label="Title / Frame 1 (EN)" value={data.hero_title_en} onChange={(v) => upd("hero_title_en", v)} testid="hero-title-en" />
                    <FieldArea label="Subtitle (AR)" value={data.hero_subtitle_ar} onChange={(v) => upd("hero_subtitle_ar", v)} testid="hero-subtitle-ar" />
                    <FieldArea label="Subtitle (EN)" value={data.hero_subtitle_en} onChange={(v) => upd("hero_subtitle_en", v)} testid="hero-subtitle-en" />
                    <Field label="Tagline / Frame 3 (AR)" value={data.hero_tagline_ar} onChange={(v) => upd("hero_tagline_ar", v)} testid="hero-tagline-ar" />
                    <Field label="Tagline / Frame 3 (EN)" value={data.hero_tagline_en} onChange={(v) => upd("hero_tagline_en", v)} testid="hero-tagline-en" />
                </div>
            </Section>

            <Section title="Section Copy" hint="Edit the public section overlines, titles, and editable body text in Arabic and English.">
                <div className="space-y-8">
                    {sectionTitleFields.map(([key, label]) => (
                        <div key={key} className="border-t border-black/10 pt-6 first:border-t-0 first:pt-0">
                            <h3 className="heading text-base tracking-tight font-bold mb-4">{label}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Field
                                    label="Overline (AR)"
                                    value={data[`section_${key}_overline_ar`]}
                                    onChange={(v) => upd(`section_${key}_overline_ar`, v)}
                                    testid={`section-${key}-overline-ar`}
                                />
                                <Field
                                    label="Overline (EN)"
                                    value={data[`section_${key}_overline_en`]}
                                    onChange={(v) => upd(`section_${key}_overline_en`, v)}
                                    testid={`section-${key}-overline-en`}
                                />
                                <Field
                                    label="Title (AR)"
                                    value={data[`section_${key}_title_ar`]}
                                    onChange={(v) => upd(`section_${key}_title_ar`, v)}
                                    testid={`section-${key}-title-ar`}
                                />
                                <Field
                                    label="Title (EN)"
                                    value={data[`section_${key}_title_en`]}
                                    onChange={(v) => upd(`section_${key}_title_en`, v)}
                                    testid={`section-${key}-title-en`}
                                />
                                {key === "about" && (
                                    <>
                                        <FieldArea
                                            label="Body Text (AR)"
                                            value={data.section_about_body_ar}
                                            onChange={(v) => upd("section_about_body_ar", v)}
                                            rows={6}
                                            testid="section-about-body-ar"
                                        />
                                        <FieldArea
                                            label="Body Text (EN)"
                                            value={data.section_about_body_en}
                                            onChange={(v) => upd("section_about_body_en", v)}
                                            rows={6}
                                            testid="section-about-body-en"
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title="معلومات التواصل" hint="تظهر في الـ Footer وصفحة Contact.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="البريد الإلكتروني" value={data.contact_email} onChange={(v) => upd("contact_email", v)} placeholder="info@Legends.sa" testid="contact-email" />
                    <Field label="رقم الهاتف" value={data.contact_phone} onChange={(v) => upd("contact_phone", v)} placeholder="+966 ..." testid="contact-phone" />
                    <Field label="العنوان (AR)" value={data.contact_address_ar} onChange={(v) => upd("contact_address_ar", v)} testid="contact-address-ar" />
                    <Field label="العنوان (EN)" value={data.contact_address_en} onChange={(v) => upd("contact_address_en", v)} testid="contact-address-en" />
                </div>
            </Section>

            <Section title="روابط التواصل الاجتماعي" hint="ضع رابط الصفحة كاملاً (https://...). اترك الحقل فارغاً لإخفاء الأيقونة.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="LinkedIn" value={data.social_linkedin} onChange={(v) => upd("social_linkedin", v)} placeholder="https://linkedin.com/company/..." testid="social-linkedin" />
                    <Field label="Instagram" value={data.social_instagram} onChange={(v) => upd("social_instagram", v)} placeholder="https://instagram.com/..." testid="social-instagram" />
                    <Field label="X (Twitter)" value={data.social_x} onChange={(v) => upd("social_x", v)} placeholder="https://x.com/..." testid="social-x" />
                    <Field label="YouTube" value={data.social_youtube} onChange={(v) => upd("social_youtube", v)} placeholder="https://youtube.com/@..." testid="social-youtube" />
                    <Field label="WhatsApp" value={data.social_whatsapp} onChange={(v) => upd("social_whatsapp", v)} placeholder="+966555... or https://wa.me/966555..." testid="social-whatsapp" />
                </div>
            </Section>

            <Section title="إعدادات SEO" hint="عناوين الـ meta لمحركات البحث + صورة OpenGraph + Canonical.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Meta Title (AR)" value={data.seo_title_ar} onChange={(v) => upd("seo_title_ar", v)} testid="seo-title-ar" />
                    <Field label="Meta Title (EN)" value={data.seo_title_en} onChange={(v) => upd("seo_title_en", v)} testid="seo-title-en" />
                    <FieldArea label="Meta Description (AR)" value={data.seo_description_ar} onChange={(v) => upd("seo_description_ar", v)} testid="seo-desc-ar" />
                    <FieldArea label="Meta Description (EN)" value={data.seo_description_en} onChange={(v) => upd("seo_description_en", v)} testid="seo-desc-en" />
                    <FileUpload value={data.seo_og_image} onChange={(v) => upd("seo_og_image", v)} label="OG Image (1200×630)" testid="og-image-upload" />
                    <Field label="Canonical URL" value={data.seo_canonical_url} onChange={(v) => upd("seo_canonical_url", v)} placeholder="https://legends.sa" testid="seo-canonical" />
                </div>
                <div className="text-xs text-[#6B7280] border-t border-black/10 pt-4">
                    Sitemap متاح على: <code className="text-[#0060A7]">/api/sitemap.xml</code> &nbsp;·&nbsp;
                    Robots: <code className="text-[#0060A7]">/api/robots.txt</code> (تُولَّد تلقائياً)
                </div>
            </Section>

            <button
                onClick={save}
                data-testid="site-settings-save"
                className="bg-[#002951] hover:bg-[#0060A7] text-white px-8 py-3 text-sm uppercase tracking-[0.2em]"
            >
                حفظ الإعدادات
            </button>
        </div>
    );
}
